import React, { useEffect, useState } from "react";
import api from "../api";
import { Bot, CheckCircle2, RefreshCw, Sparkles, AlertCircle } from "lucide-react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

/**
 * Admin · Turbo AI Pending Review Queue — Step 13
 * Every low-confidence query (< 85 %) is silently staged here. Once the admin
 * types a canonical answer, it gets permanently indexed into the RAG core.
 */
export default function AdminAiPendingPage() {
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [answers, setAnswers] = useState({});   // pending_id → draft answer
  const [topics, setTopics] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/support/admin/pending"),
        api.get("/support/kb/stats"),
      ]);
      setPending(p.data.pending || []);
      setStats(s.data);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 20000);

  const resolve = async (p) => {
    const answer = (answers[p.pending_id] || "").trim();
    if (answer.length < 10) { alert("Answer must be at least 10 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/support/admin/answer/${p.pending_id}`, {
        answer,
        topic: topics[p.pending_id] || p.best_guess_topic || "learned.custom",
        canonical_question: p.query,
      });
      setAnswers((a) => { const n = { ...a }; delete n[p.pending_id]; return n; });
      setTopics((a) => { const n = { ...a }; delete n[p.pending_id]; return n; });
      await load();
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const reindex = async () => {
    setBusy(true);
    try { await api.post("/support/admin/reindex"); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-ai-pending-page">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <Bot size={26} color="#00F2FF" />
        <h2 className="page-title" style={{ margin: 0 }}>Turbo AI · Pending Review</h2>
      </div>
      <p className="page-sub" style={{ marginTop: 6 }}>
        Low-confidence merchant queries stage here. Your answer is permanently indexed into the RAG core (TF-IDF vector re-fit).
      </p>

      {/* Stats banner */}
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <div className="tg-card" style={{ padding: 16, background: "linear-gradient(135deg, rgba(0,242,255,0.10), rgba(255,255,255,0.02))", borderColor: "#00F2FF30" }}>
          <div style={{ fontSize: 11, color: "#00F2FF", letterSpacing: 1 }}>KB TOTAL</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>{stats?.total_entries ?? "—"}</div>
          <div style={{ fontSize: 10, color: "#5F6884" }}>entries indexed</div>
        </div>
        <div className="tg-card" style={{ padding: 16, background: "linear-gradient(135deg, rgba(255,215,0,0.10), rgba(255,255,255,0.02))", borderColor: "#FFD70030" }}>
          <div style={{ fontSize: 11, color: "#FFD700", letterSpacing: 1 }}>SEEDED</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>{stats?.seeded ?? "—"}</div>
          <div style={{ fontSize: 10, color: "#5F6884" }}>bootstrapped</div>
        </div>
        <div className="tg-card" style={{ padding: 16, background: "linear-gradient(135deg, rgba(16,244,167,0.10), rgba(255,255,255,0.02))", borderColor: "#10F4A730" }}>
          <div style={{ fontSize: 11, color: "#10F4A7", letterSpacing: 1 }}>LEARNED</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>{stats?.learned ?? "—"}</div>
          <div style={{ fontSize: 10, color: "#5F6884" }}>admin-added</div>
        </div>
        <div className="tg-card" style={{ padding: 16, background: "linear-gradient(135deg, rgba(255,61,108,0.10), rgba(255,255,255,0.02))", borderColor: "#FF3D6C30" }}>
          <div style={{ fontSize: 11, color: "#FF3D6C", letterSpacing: 1 }}>PENDING</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>{pending.length}</div>
          <div style={{ fontSize: 10, color: "#5F6884" }}>awaiting your answer</div>
        </div>
      </div>

      <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <button data-testid="admin-ai-reindex" onClick={reindex} disabled={busy}
          style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(0,242,255,0.35)", background: "transparent", color: "#00F2FF", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <RefreshCw size={13} /> Force Reindex
        </button>
      </div>

      {/* Pending list */}
      <div className="tg-card" data-testid="admin-ai-pending-list" style={{ padding: 22 }}>
        {pending.length === 0 && (
          <div style={{ textAlign: "center", color: "#5F6884", padding: 30, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <CheckCircle2 size={28} color="#10F4A7" />
            <div style={{ color: "#10F4A7", fontWeight: 700 }}>Queue clear</div>
            <div style={{ fontSize: 12 }}>All merchant questions are auto-resolved from the knowledge base.</div>
          </div>
        )}
        {pending.map((p) => (
          <div key={p.pending_id} data-testid={`pending-${p.pending_id}`}
            style={{ padding: 16, marginBottom: 14, borderRadius: 12,
                     background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,215,0,0.30)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "#5F6884", letterSpacing: 1, textTransform: "uppercase" }}>Merchant query · lang={p.lang} · conf {Math.round((p.confidence || 0) * 100)}%</div>
                <div style={{ fontSize: 14, color: "#E7ECFF", marginTop: 6, fontWeight: 700 }}>{p.query}</div>
                <div style={{ fontSize: 10, color: "#5F6884", marginTop: 4 }}>
                  from <code>{p.user_email || p.user_id}</code> · {new Date(p.created_at).toLocaleString()}
                </div>
              </div>
              <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, background: "#FFD70033", color: "#FFD700" }}>{p.status}</span>
            </div>
            {p.best_guess && (
              <div style={{ fontSize: 11, color: "#8B94B8", background: "rgba(255,255,255,0.03)", padding: 8, borderRadius: 8, marginBottom: 10 }}>
                <b style={{ color: "#8B94B8" }}>Best-guess ({p.best_guess_topic || "—"}):</b> {p.best_guess.slice(0, 220)}{p.best_guess.length > 220 ? "…" : ""}
              </div>
            )}
            <textarea data-testid={`answer-input-${p.pending_id}`}
              value={answers[p.pending_id] || ""}
              onChange={(e) => setAnswers((a) => ({ ...a, [p.pending_id]: e.target.value }))}
              placeholder="Canonical answer — will be permanently indexed…"
              style={{ width: "100%", height: 100, padding: 10, marginBottom: 8, borderRadius: 8,
                       background: "rgba(0,0,0,0.30)", border: "1px solid rgba(0,242,255,0.20)",
                       color: "#E7ECFF", fontSize: 13, fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid={`topic-input-${p.pending_id}`}
                value={topics[p.pending_id] || ""}
                onChange={(e) => setTopics((a) => ({ ...a, [p.pending_id]: e.target.value }))}
                placeholder={`topic (default: ${p.best_guess_topic || "learned.custom"})`}
                style={{ flex: 1, padding: 10, borderRadius: 8,
                         background: "rgba(0,0,0,0.30)", border: "1px solid rgba(0,242,255,0.20)",
                         color: "#E7ECFF", fontSize: 12 }} />
              <button data-testid={`resolve-${p.pending_id}`} onClick={() => resolve(p)} disabled={busy}
                style={{ padding: "10px 16px", borderRadius: 8, border: "none",
                         background: "linear-gradient(135deg, #10F4A7, #00F2FF)", color: "#050914",
                         fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer",
                         display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={13} /> Index & Resolve
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
